import { BriefingTemplateSnapshot, SponsorView } from "../../Types/AccountPlan";

/** How a briefing is laid out: which sections, in what order, titled and numbered how. */

export type LayoutPersona = "ES" | "AE" | "CSM";

export interface LayoutSection {
  id: string;
  /** The template's title; null = the section's own built-in title. */
  title: string | null;
  /** Position among the briefing's sections, the same for every persona. */
  num: number;
  collapsedForCsm: boolean;
  custom: boolean;
}

// Before templates: the fixed nine sections.
const FIXED = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8", "s9"];
const FIXED_CSM_COLLAPSED = ["s4", "s5", "s7"];
/** CSM leads with the section it owns. */
const CSM_FIRST = "s8";
const KNOWN = new Set(FIXED);

/**
 * The briefing's sections for a persona. From the template the briefing was generated
 * with: its order and titles, hidden sections left out, custom sections included. A
 * briefing without one keeps the fixed layout.
 */
export function briefingLayout(
  view: Pick<SponsorView, "template"> | null | undefined,
  persona: LayoutPersona
): LayoutSection[] {
  const tpl = view?.template?.sections;
  const all: LayoutSection[] = tpl?.length
    ? tpl
        .filter(s => s.enabled && (s.custom || KNOWN.has(s.key)))
        .map((s, i) => ({
          id: s.key,
          title: s.title || null,
          num: i + 1,
          collapsedForCsm: s.collapsed_for_csm,
          custom: s.custom,
        }))
    : FIXED.map((id, i) => ({
        id,
        title: null,
        num: i + 1,
        collapsedForCsm: FIXED_CSM_COLLAPSED.indexOf(id) >= 0,
        custom: false,
      }));
  if (persona !== "CSM") return all;
  const first = all.filter(s => s.id === CSM_FIRST);
  return first.concat(all.filter(s => s.id !== CSM_FIRST));
}

/** A briefing question the plan didn't fully answer, shown in a section it informs. */
export interface SectionGap {
  questionId: string;
  question: string;
  /** missing = not in the plan; partial = answered with a gap. */
  status: "missing" | "partial";
}

// The built-in questions' sections, for briefings made before questions named them
// (backend services/account_plans/template.py _INFORMS).
const DEFAULT_INFORMS: Record<string, string[]> = {
  bq1: ["s2", "s4", "s8"],
  bq2: ["s9"],
  bq3: ["s9"],
  bq4: ["s9"],
  bq5: ["s2", "s9"],
};

/**
 * Each section's gaps: the briefing questions that inform it and that the plan
 * leaves missing or partly answered (design note §3.8). Same rule as the Q&A
 * coverage: missing-from-deck is missing, a gap callout is partial.
 */
export function sectionGaps(
  view: Pick<SponsorView, "template" | "briefing"> | null | undefined
): Record<string, SectionGap[]> {
  const out: Record<string, SectionGap[]> = {};
  const informs = new Map<string, string[]>();
  (view?.template?.questions || []).forEach(q => {
    if (q.informs) informs.set(q.key, q.informs);
  });
  (view?.briefing?.answers || []).forEach(a => {
    const status =
      a.source_flag === "missing-from-deck"
        ? "missing"
        : a.gap_callout
          ? "partial"
          : null;
    if (!status) return;
    const sections =
      informs.get(a.question_id) || DEFAULT_INFORMS[a.question_id] || [];
    sections.forEach(sid => {
      (out[sid] = out[sid] || []).push({
        questionId: a.question_id,
        question: a.question,
        status,
      });
    });
  });
  return out;
}

/** "Briefing template v3" and the like: which template a draft was generated with. */
export function templateLabel(
  tpl: BriefingTemplateSnapshot | null | undefined
): string | null {
  if (!tpl) return null;
  if (tpl.source === "code") return "standard briefing template";
  const n = tpl.version_no ? ` v${tpl.version_no}` : "";
  return tpl.source === "default"
    ? `default briefing template${n}`
    : `briefing template${n}`;
}
